import { Box, Checkbox, FormControlLabel, FormHelperText, TextField } from '@mui/material';
import { FormikErrors, FormikProps } from 'formik';
import { ClipboardEvent, KeyboardEvent, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import { formatPhone, normalizePhone } from 'utils/phone-format-helpers';
import { FormDialog } from './form-dialog';
import 'react-phone-input-2/lib/material.css';

type FormValues = {
  phone: string;
  isExtension: boolean;
  ext: string;
};

const initialValues: FormValues = {
  phone: '+7',
  isExtension: false,
  ext: '',
};

type DialogProps = {
  open: boolean;
  onSubmit: (phone: string, ext?: string) => Promise<void>;
  onClose: () => void;
};

export const PhoneDialog = ({ open, onSubmit, onClose }: DialogProps) => {
  const handleSubmit = (values: FormValues) => {
    const phone = normalizePhone(values.phone);
    if (!phone) return Promise.reject(new Error('Неверный формат номера телефона'));
    return onSubmit(phone, values.isExtension ? values.ext : undefined).then(onClose);
  };

  return (
    <FormDialog
      key={open ? 'open' : 'closed'}
      open={open}
      onClose={onClose}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      validateOnChange
      submitOnEnter
      renderTitle="Добавление нового телефона"
      renderForm={(props) => <FormInput formikProps={props} />}
      dialogProps={{ maxWidth: 'xs', fullWidth: true }}
    />
  );
};

function validate(values: FormValues): FormikErrors<FormValues> {
  const phone = normalizePhone(values.phone);
  if (!phone || !phone.startsWith('+7')) return { phone: 'Неверный формат' };
  if (values.isExtension && !/^\d{1,20}$/.test(values.ext)) return { ext: 'Введите от 1 до 20 цифр' };
  return {};
}

const FormInput = ({ formikProps: props }: { formikProps: FormikProps<FormValues> }) => {
  const hasInteracted = useRef(false);
  const extensionInput = useRef<HTMLInputElement>(null);
  const normalizedPhone = normalizePhone(props.values.phone);
  const error = hasInteracted.current || props.submitCount > 0 ? props.errors.phone : undefined;

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    hasInteracted.current = true;
    const pasted = event.clipboardData.getData('text/plain') || event.clipboardData.getData('text');
    const separator = pasted.indexOf('#');
    const text = (separator < 0 ? pasted : pasted.slice(0, separator)).replace(
      /[\s().\-\u2010-\u2015\u2212\u200b-\u200f\u202a-\u202e\u2066-\u2069]/g,
      '',
    );
    const ext = separator < 0 ? '' : pasted.slice(separator + 1).trim();
    if (!/^\+?\d+$/.test(text) || (text.startsWith('+') && !text.startsWith('+7'))) {
      event.preventDefault();
      props.setFieldTouched('phone', true, false);
      props.setFieldError('phone', text.startsWith('+') ? 'Введите полный номер с кодом +7' : 'Неверный формат');
      return;
    }

    const digits = text.replace(/^\+/, '');
    const includesCountryCode = text.startsWith('+7') || (digits.length >= 11 && /^[78]/.test(digits));
    if (includesCountryCode || digits.length >= 10 || separator >= 0) {
      event.preventDefault();
      const subscriberDigits = includesCountryCode ? digits.slice(1) : digits;
      props.setValues({ ...props.values, phone: `+7${subscriberDigits}`, isExtension: separator >= 0, ext });
    }
  };

  return (
    <Box>
      <PhoneInput
        country="ru"
        onlyCountries={['ru']}
        disableDropdown
        disableCountryGuess
        countryCodeEditable={false}
        enableLongNumbers
        masks={{ ru: '... ... .. ..' }}
        specialLabel="Телефон"
        placeholder="+7 999 123 45 67"
        value={props.values.phone}
        onChange={(digits) => {
          hasInteracted.current = true;
          props.setFieldValue('phone', digits ? `+${digits}` : '+7');
        }}
        onBlur={() => {
          if (hasInteracted.current) props.setFieldTouched('phone', true);
        }}
        disabled={props.isSubmitting}
        isValid={!error}
        defaultErrorMessage=""
        inputStyle={{ width: '100%' }}
        inputProps={{
          id: 'phone',
          name: 'phone',
          'aria-label': 'Телефон',
          'aria-invalid': Boolean(error),
          'aria-describedby': 'phone-help',
          autoFocus: true,
          autoComplete: 'tel',
          onPaste: handlePaste,
          onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === '#') {
              event.preventDefault();
              props.setFieldValue('isExtension', true);
              setTimeout(() => extensionInput.current?.focus(), 0);
            }
          },
        }}
      />
      <FormHelperText id="phone-help" error={Boolean(error)}>
        {error || (normalizedPhone ? `Будет сохранён как: ${formatPhone(normalizedPhone)}${props.values.isExtension && props.values.ext ? `#${props.values.ext}` : ''}` : 'Введите 10 цифр номера после +7')}
      </FormHelperText>
      <FormControlLabel
        label="Сохранить как добавочный"
        control={
          <Checkbox name="isExtension" checked={props.values.isExtension} onChange={(_, checked) => props.setFieldValue('isExtension', checked)} disabled={props.isSubmitting} />
        }
      />
      {props.values.isExtension && (
        <TextField
          fullWidth
          margin="normal"
          name="ext"
          label="Добавочный номер"
          value={props.values.ext}
          onChange={props.handleChange}
          onBlur={props.handleBlur}
          inputRef={extensionInput}
          inputProps={{ inputMode: 'numeric', autoComplete: 'tel-extension' }}
          disabled={props.isSubmitting}
          error={Boolean(props.errors.ext && (props.touched.ext || props.values.ext || props.submitCount))}
          helperText={(props.touched.ext || props.values.ext || props.submitCount) ? props.errors.ext : undefined}
        />
      )}
    </Box>
  );
};
